<template>
  <div class="inquiry-form-container">
    <h2 class="form-title">{{ $t("inquiryForm") }}</h2>
    <form
      class="inquiry-form-wrapper"
      @submit.prevent="handleSubmit"
      novalidate
    >
      <div class="form-grid">
        <!-- Form fields unchanged -->
        <div class="form-group">
          <label for="firstName">{{ $t("firstName") }}:</label>
          <input
            id="firstName"
            v-model.trim="form.firstName"
            type="text"
            required
            :class="{ 'is-invalid': errors.firstName }"
          />
          <span v-if="errors.firstName" class="error-msg">{{
            errors.firstName
          }}</span>
        </div>
        <div class="form-group">
          <label for="lastName">{{ $t("lastName") }}:</label>
          <input
            id="lastName"
            v-model.trim="form.lastName"
            type="text"
            required
            :class="{ 'is-invalid': errors.lastName }"
          />
          <span v-if="errors.lastName" class="error-msg">{{
            errors.lastName
          }}</span>
        </div>
        <div class="form-group full-width">
          <label for="company">{{ $t("company") }}:</label>
          <input
            id="company"
            v-model.trim="form.company"
            type="text"
            required
            :class="{ 'is-invalid': errors.company }"
          />
          <span v-if="errors.company" class="error-msg">{{
            errors.company
          }}</span>
        </div>
        <div class="form-group full-width">
          <label for="email">{{ $t("bemail") }}:</label>
          <input
            id="email"
            v-model.trim="form.email"
            type="email"
            required
            :class="{ 'is-invalid': errors.email }"
          />
          <span v-if="errors.email" class="error-msg">{{ errors.email }}</span>
        </div>
        <div class="form-group">
          <label for="country">{{ $t("country") }}:</label>
          <select
            id="country"
            v-model="form.country"
            required
            :class="{ 'is-invalid': errors.country }"
          >
            <option value="">- {{ $t("select-location") }} -</option>
            <option
              v-for="country in countries"
              :key="country"
              :value="country"
            >
              {{ $t("countries." + country) }}
            </option>
          </select>
          <span v-if="errors.country" class="error-msg">{{
            errors.country
          }}</span>
        </div>
        <div class="form-group distributor-inline">
          <label>{{ $t("Preferred-Distributor") }}:</label>
          <span class="distributor-value">{{ $t("Digi-Key") }}</span>
        </div>
        <div class="form-group full-width">
          <label for="phone">{{ $t("phone") }}:</label>
          <input
            id="phone"
            v-model.trim="form.phone"
            type="tel"
            placeholder="+1 234 567 8901"
          />
        </div>
        <div class="form-group full-width">
          <label>
            <input type="checkbox" v-model="form.receiveQuote" />
            {{ $t("receive-offer") }}
          </label>
        </div>
        <div class="form-group full-width">
          <label>
            <input type="checkbox" v-model="form.receiveSample" />
            {{ $t("newsletter") }}
          </label>
        </div>
        <div class="form-group full-width">
          <label>
            <input type="checkbox" v-model="form.acceptTerms" required />
            {{ $t("privacy-policy") }}
            <a href="#" @click.prevent="openPrivacyModal">{{
              $t("privacy-policy-title")
            }}</a>
          </label>
          <span v-if="errors.acceptTerms" class="error-msg">{{
            errors.acceptTerms
          }}</span>
        </div>
        <div class="form-group full-width">
          <div id="recaptcha-container"></div>
          <span v-if="errors.recaptcha" class="error-msg">{{
            errors.recaptcha
          }}</span>
        </div>
      </div>

      <button type="submit" :disabled="isSubmitting" class="submit-btn">
        {{ isSubmitting ? $t("button.sending") : $t("button.submit") }}
      </button>
      <div v-if="errorMessage" class="error-msg">{{ errorMessage }}</div>
    </form>

    <!-- Privacy Policy Modal -->
    <div
      v-if="showPrivacyModal"
      class="modal-overlay"
      @click.self="closePrivacyModal"
    >
      <div
        class="modal-content"
        role="dialog"
        aria-modal="true"
        aria-labelledby="privacyTitle"
      >
        <button
          class="modal-close-btn"
          @click="closePrivacyModal"
          aria-label="Close Privacy Policy"
        >
          &times;
        </button>
        <PrivacyPolicy @close="closePrivacyModal" />
      </div>
    </div>

    <!-- Success Message Modal -->
    <div
      v-if="showSuccessModal"
      class="modal-overlay"
      @click.self="closeSuccessModal"
    >
      <div class="modal-content" role="dialog" aria-modal="true">
        <button
          class="modal-close-btn"
          @click="closeSuccessModal"
          aria-label="Close Success Message"
        >
          &times;
        </button>
        <div v-html="successMessage"></div>
      </div>
    </div>
  </div>
</template>

<script>
import PrivacyPolicy from "./privacyPolicy.vue";
export default {
  components: {
    PrivacyPolicy,
  },
  props: {
    productDetail: {
      type: [String, Object],
      required: false,
    },
    price: {
      type: [String, Number],
      required: false,
    },
    quantity: {
      type: [String, Number],
      required: false,
    },
    currency_adjustment_percent: {
       type: [String, Number],
      required: false,
    },
    surchargeDigikey:{
      type: [String, Number],
      required: false
    },
    language: {
       type: [String, Number],
      required: false
    },
    product_description: {
       type: [String, Number],
      required: false
    },
    cableLength:{
      type: [String, Number],
      required: false
    }
  },
  data() {
    return {
      form: {
        firstName: "",
        lastName: "",
        company: "",
        email: "",
        country: "",
        phone: "",
        receiveQuote: false,
        receiveSample: false,
        acceptTerms: false,
      },
      showPrivacyModal: false,
      showSuccessModal: false,
      successMessage: "",
      totalPrice: 0,
      unitPrice: 0,
      errors: {},
      isSubmitting: false,
      errorMessage: "",
      recaptchaWidgetId: null,
      countries: [
        "Austria",
        "Belgium",
        "Bulgaria",
        "Croatia",
        "Cyprus",
        "Czech Republic",
        "Denmark",
        "Estonia",
        "Finland",
        "France",
        "Germany",
        "Greece",
        "Hungary",
        "Ireland",
        "Italy",
        "Latvia",
        "Lithuania",
        "Luxembourg",
        "Malta",
        "Netherlands",
        "Poland",
        "Portugal",
        "Romania",
        "Slovakia",
        "Slovenia",
        "Spain",
        "Sweden",
      ],
    };
  },
  mounted() {
    let attempts = 0;
    const loadRecaptcha = () => {
      if (window.grecaptcha) {
        this.recaptchaWidgetId = window.grecaptcha.render(
          "recaptcha-container",
          {
            sitekey: "6Ld1M5wrAAAAANgyzHTfBpEncH-S6X7LDuyP97-u",
          }
        );
      } else if (++attempts <= 10) {
        setTimeout(loadRecaptcha, 500);
      }
    };
    loadRecaptcha();
  },
  methods: {
    validate() {
      const errors = {};
      if (!this.form.firstName)
        errors.firstName = this.$t("errors.firstNameRequired");
      if (!this.form.lastName)
        errors.lastName = this.$t("errors.lastNameRequired");
      if (!this.form.company)
        errors.company = this.$t("errors.companyRequired");
      if (!this.form.email) errors.email = this.$t("errors.emailRequired");
      else if (!/^[\w-.]+@([\w-]+\.)+[\w-]{2,}$/.test(this.form.email))
        errors.email = this.$t("errors.invalidEmail");
      if (!this.form.country)
        errors.country = this.$t("errors.countryRequired");
      if (!this.form.acceptTerms)
        errors.acceptTerms = this.$t("errors.acceptTerms");
      if (!window.grecaptcha)
        errors.recaptcha = this.$t("errors.recaptchaNotLoaded");
      else if (!window.grecaptcha.getResponse(this.recaptchaWidgetId))
        errors.recaptcha = this.$t("errors.recaptchaNotVerified");
      this.errors = errors;
      return Object.keys(errors).length === 0;
    },

    async handleSubmit() {
      this.errorMessage = "";
      if (!this.validate()) return;

      this.isSubmitting = true;
      try {
        const recaptchaToken = window.grecaptcha.getResponse(
          this.recaptchaWidgetId
        );
        // Calculate unit price (price with surcharge and currency adjustment, without quantity)
        const unitPrice =
          parseFloat(this.price.toFixed(2)) *
          (1 + this.surchargeDigikey / 100) *
          (1 + this.currency_adjustment_percent / 100);

        // Calculate total price (unit price * quantity)
        const totalPrice = unitPrice * this.quantity;

        // Format prices for EUR currency with German locale
        this.unitPrice = new Intl.NumberFormat("de-DE", {
          style: "currency",
          currency: "EUR",
        }).format(unitPrice);

        this.totalPrice = new Intl.NumberFormat("de-DE", {
          style: "currency",
          currency: "EUR",
        }).format(totalPrice);
        const formData = new FormData();
        Object.entries(this.form).forEach(([key, value]) => {
          formData.append(key, value);
        });
        formData.append("productDetail", this.product_description);
        formData.append("price", this.unitPrice);
        formData.append("quantity", this.quantity);
        formData.append('unitPrice', this.unitPrice); // Formatted unit price 
        formData.append('totalPrice', this.totalPrice); // Formatted total price
        formData.append("token", recaptchaToken);
        formData.append("language", this.language);
        formData.append("cableLength", this.cableLength);
        formData.append("product_description", this.product_description);

        const data = Object.fromEntries(formData);

        const response = await fetch(
          "https://cdn.sachsenkabel.de/flows/trigger/9004e917-c7f9-40d1-b700-8bef632e2743",
          {
            method: "POST",
            body: JSON.stringify(data),
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer LQLAk5ZEiND_i7hOXkjY_UsbSMPW-zWs",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Network response was not ok");
        }

        // Success Message in Modal
        this.successMessage =
          '<div style="text-align:left">' +
          '<div style="font-size:2.2rem;color:#e60b27;font-weight:bold;margin-top:2rem;">Custom Cable Creator</div>' +
          '<div style="margin-top:1.5rem;font-size:1.2rem;color:#22884f;font-weight:bold;">Thank you for designing a cable using our Custom Cable Creator.</div>' +
          '<div style="margin-top:1.2rem;font-size:1.05rem;color:#222;">' +
          "Our Cable Creation team is reviewing your submission and will send a confirmation email with the details for your cable design.<br/><br/>" +
          'If you would like to design an additional cable, <a href="">Click here</a> to return to the Custom Cable Creator!' +
          "</div>" +
          "</div>";

        this.showSuccessModal = true;
        this.form = {
          firstName: "",
          lastName: "",
          company: "",
          email: "",
          country: "",
          phone: "",
          receiveQuote: false,
          receiveSample: false,
          acceptTerms: false,
        };
        window.grecaptcha.reset(this.recaptchaWidgetId);
        document.body.style.overflow = "hidden"; // prevent background scroll when modal is open
      } catch {
        this.errorMessage = "Submission failed. Please try again.";
      } finally {
        this.isSubmitting = false;
      }
    },
    post(url, data) {
      return fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });
    },
    openPrivacyModal() {
      this.showPrivacyModal = true;
      document.body.style.overflow = "hidden";
    },
    closePrivacyModal() {
      this.showPrivacyModal = false;
      document.body.style.overflow = "";
    },
    closeSuccessModal() {
      this.showSuccessModal = false;
      document.body.style.overflow = "";
      this.$router.push({ path: "/" });
    },
  },
};
</script>

<style scoped>
.inquiry-form-container {
  max-width: 960px;
  margin: 0 auto;
  padding: 2rem 1rem;
}
.form-title {
  text-align: center;
  font-size: 1.8rem;
  font-weight: bold;
  color: #0059b3;
  margin-bottom: 1.5rem;
}
.inquiry-form-wrapper {
  background: #fff;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}
.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 0.5rem 1.5rem;
}
.form-group {
  display: flex;
  flex-direction: column;
  margin-bottom: 0rem;
}
.full-width {
  grid-column: 1 / -1;
}
label {
  font-weight: 600;
  margin-bottom: 0.25rem;
  color: #333;
}
input,
select {
  padding: 0.5em 0.75em;
  font-size: 1rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-sizing: border-box;
  transition: border-color 0.2s, box-shadow 0.2s;
}
input:focus,
select:focus {
  border-color: #0059b3;
  outline: none;
  box-shadow: 0 0 0 2px rgba(0, 89, 179, 0.1);
}
.is-invalid {
  border-color: #c0392b;
}
.submit-btn {
  background: #0059b3;
  color: white;
  border: none;
  padding: 0.8em 1.2em;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  margin-top: 1.5rem;
}
.submit-btn:hover:not([disabled]) {
  background: #004080;
}
.submit-btn[disabled] {
  opacity: 0.6;
  cursor: not-allowed;
}
.error-msg {
  color: #c0392b;
  font-size: 0.875rem;
  margin-top: 0.25rem;
}
.success-msg {
  color: #22884f;
  margin-top: 1.25rem;
  font-weight: 600;
  font-size: 1rem;
  text-align: center;
}
.distributor-inline {
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
}
.distributor-inline label {
  margin-bottom: 0;
  min-width: 140px;
}
.distributor-value {
  font-weight: 500;
  color: #444;
  font-size: 1rem;
}
@media (max-width: 500px) {
  .inquiry-form-wrapper {
    padding: 1.25rem;
  }
  .submit-btn {
    width: 100%;
  }
}
/* Modal Background */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  padding: 1rem;
}
/* Modal Content Box */
.modal-content {
  background: white;
  max-width: 800px;
  width: 100%;
  max-height: 90vh; /* allows modal to be scrollable */
  overflow-y: auto;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  border-radius: 8px;
  position: relative;
  padding: 2rem;
}
/* Close Button */
.modal-close-btn {
  position: absolute;
  top: 1rem;
  right: 1rem;
  font-size: 1.8rem;
  background: transparent;
  border: none;
  cursor: pointer;
  color: #555;
  z-index: 10;
}
.modal-close-btn:hover {
  color: #000;
}
/* Responsive tweak */
@media (max-width: 480px) {
  .modal-content {
    max-width: 95vw;
    padding: 1rem;
  }
}
</style>
